import { useState } from 'react';
import {
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { StudentEmployerPlacementStatus } from '@duke-hq/svc-api-db/client';
import { useCheckPermission } from '@duke-hq/app-admin-web/hooks/useCheckPermission';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Ban, BadgeCheck, CircleX, Ellipsis, FileText, ListChecks, Plus } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { ManageUnitsModal } from './ManageUnitsModal';
import { PlacementActionModal, type PlacementActionStatus } from './PlacementActionModal';
import { testIds } from './testIds';

type ActivePlacement = {
  id: string;
  status: StudentEmployerPlacementStatus;
  units: { unitOfCompetencyId: string }[];
};

export function PlacementActions({
  activePlacement,
  canPlaceStudent,
  placeBlockedReason,
  courseEnrolmentId,
  onPlace,
  onChanged,
}: {
  activePlacement: ActivePlacement | null;
  canPlaceStudent: boolean;
  placeBlockedReason: string | null;
  courseEnrolmentId: number;
  onPlace: () => void;
  onChanged: () => void;
}): React.ReactElement {
  const [action, setAction] = useState<PlacementActionStatus | null>(null);
  const manageUnitsModal = useDisclosure();
  const canAssessWorkPlacements = useCheckPermission('canAssessWorkPlacements');

  const placementStarted =
    activePlacement != null &&
    (activePlacement.status === StudentEmployerPlacementStatus.IN_PROGRESS ||
      activePlacement.status === StudentEmployerPlacementStatus.AWAITING_EVIDENCE);

  return (
    <>
      <HStack gap={1}>
        <Tooltip
          label={placeBlockedReason}
          isDisabled={placeBlockedReason == null}
          shouldWrapChildren
        >
          <Button
            leftIcon={<Plus size={iconSize.md} />}
            onClick={onPlace}
            isDisabled={!canPlaceStudent}
            data-testid={testIds.placeStudentButton}
          >
            <Trans>Place student in workplace</Trans>
          </Button>
        </Tooltip>

        <Menu isLazy>
          <MenuButton
            as={IconButton}
            aria-label={t`placement actions`}
            icon={<Icon as={Ellipsis} />}
            variant="ghost"
            size="sm"
            isDisabled={!placementStarted}
            data-testid={testIds.placementActionsMenu}
          />
          <MenuList>
            {activePlacement?.status === StudentEmployerPlacementStatus.IN_PROGRESS ? (
              <MenuItem
                icon={<FileText size={iconSize.md} />}
                onClick={() => setAction(StudentEmployerPlacementStatus.AWAITING_EVIDENCE)}
                data-testid={testIds.requestEvidenceButton}
              >
                <Trans>Request evidence</Trans>
              </MenuItem>
            ) : null}
            {canAssessWorkPlacements ? (
              <MenuItem
                icon={<ListChecks size={iconSize.md} />}
                onClick={manageUnitsModal.onOpen}
                data-testid={testIds.manageUnitsButton}
              >
                <Trans>Manage units</Trans>
              </MenuItem>
            ) : null}
            {canAssessWorkPlacements ? (
              <MenuItem
                icon={<BadgeCheck size={iconSize.md} />}
                onClick={() => setAction(StudentEmployerPlacementStatus.COMPLETED)}
                data-testid={testIds.markCompleteButton}
              >
                <Trans>Mark complete</Trans>
              </MenuItem>
            ) : null}
            <MenuItem
              icon={<CircleX size={iconSize.md} />}
              onClick={() => setAction(StudentEmployerPlacementStatus.FAILED)}
              data-testid={testIds.markFailedButton}
            >
              <Trans>Mark failed</Trans>
            </MenuItem>
            <MenuItem
              icon={<Ban size={iconSize.md} />}
              color="danger"
              bg="red.50"
              onClick={() => setAction(StudentEmployerPlacementStatus.CANCELLED)}
              data-testid={testIds.cancelPlacementButton}
            >
              <Trans>Cancel placement</Trans>
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>

      {action != null && activePlacement != null ? (
        <PlacementActionModal
          isOpen
          onClose={() => setAction(null)}
          employerPlacementId={activePlacement.id}
          targetStatus={action}
          courseEnrolmentId={courseEnrolmentId}
          unitOfCompetencyIds={activePlacement.units.map((unit) => unit.unitOfCompetencyId)}
          onSuccess={onChanged}
        />
      ) : null}

      {activePlacement != null ? (
        <ManageUnitsModal
          isOpen={manageUnitsModal.isOpen}
          onClose={manageUnitsModal.onClose}
          placementId={activePlacement.id}
          courseEnrolmentId={courseEnrolmentId}
        />
      ) : null}
    </>
  );
}
